import { useEffect, useRef, useState } from "react";
import { useDemo } from "../zustand/DemoZustand";
import { Kreuz, PfeilRechts, Textmarker } from "../bausteine/Symbole";
import { nameVon } from "../hilfen/auswertung";
import { useChat } from "../zustand/ChatZustand";
import { springeZuAnnotation } from "../hilfen/textauswahl";
import { kurzesDatum, uhrzeit } from "../hilfen/datum";

/*
  Das Gespräch in einem Kanal.

  Ein Kanal gehört zu einem Gremium — der Fraktion, dem Bau- und
  Umweltausschuss, dem Finanzausschuss —, nicht zu einem Tagesordnungspunkt.
  Er besteht die ganze Wahlperiode. Die Begründung steht in KOMMUNIKATION.md.

  Zwei Betriebsarten:

    ohne `top`   — der ganze Kanal, alle Beiträge, wie in jedem Messenger
    mit `top`    — die Rückansicht: nur die Beiträge, die auf diesen
                   Tagesordnungspunkt verweisen. Geschrieben wird trotzdem in
                   den Kanal; der Punkt hängt dann als Verweis an der
                   Nachricht.

  Die Rückansicht ist kein eigener Kanal. Derselbe Beitrag steht im
  Kanalverlauf, wo er gefallen ist, *und* beim Punkt, um den es geht — einmal
  nach Menschen sortiert, einmal nach Sache. Kopiert wird nichts.
*/

export default function Fraktionschat({
  kanalId,
  top = null,
  aufgabe = null,
  annotationId,
  aufBezug,
  eingebettet = false,
  aufZitatVerbraucht,
  aufTextstelle,
}) {
  const { stammdaten, zustand, ich } = useDemo();
  const chat = useChat();
  const [entwurf, setzeEntwurf] = useState("");
  const [sendefehler, setzeSendefehler] = useState(null);
  const [zitatId, setzeZitatId] = useState(annotationId || null);
  const feldRef = useRef(null);
  const endeRef = useRef(null);

  const kanal = (stammdaten.kanaele || []).find((k) => k.id === kanalId);

  useEffect(() => {
    if (!annotationId) return;
    setzeZitatId(annotationId);
    if (feldRef.current) feldRef.current.focus();
  }, [annotationId]);

  useEffect(() => {
    if (endeRef.current) endeRef.current.scrollIntoView({ block: "nearest" });
  }, [kanalId, top]);

  if (!kanal) {
    return (
      <div className="karte" style={{ marginTop: "1.5rem" }}>
        <p>Dieser Kanal wurde nicht gefunden.</p>
      </div>
    );
  }

  const alle = chat.nachrichten.filter((n) => n.kanalId === kanalId);
  const beitraege = aufgabe
    ? alle.filter((n) => n.aufgabeId === aufgabe.id)
    : top
      ? alle.filter((n) => n.topId === top.id)
      : alle;

  const zitat = zitatId ? zustand.annotationen.find((a) => a.id === zitatId) : null;

  function zitatLoeschen() {
    setzeZitatId(null);
    if (aufZitatVerbraucht) aufZitatVerbraucht();
  }

  /*
    Gesendet wird über Matrix, also über das Netz — das kann fehlschlagen.
    Der Entwurf bleibt deshalb stehen, bis er wirklich draußen ist. Nichts
    ist ärgerlicher, als einen getippten Beitrag zu verlieren.
  */
  async function senden() {
    const text = entwurf.trim();
    if (!text && !zitat) return;

    setzeSendefehler(null);
    try {
      await chat.sende({
        kanalId,
        topId: top ? top.id : null,
        aufgabeId: aufgabe ? aufgabe.id : null,
        text: text || "(ohne Kommentar)",
        annotationId: zitat ? zitat.id : null,
      });
      setzeEntwurf("");
      zitatLoeschen();
    } catch (fehler) {
      setzeSendefehler(fehler.message || "Der Beitrag ließ sich nicht senden.");
    }
  }

  function zurTextstelle(annotation) {
    if (springeZuAnnotation(annotation.id)) return;
    if (aufTextstelle) aufTextstelle(annotation);
  }

  return (
    <>
      {!eingebettet && (
        <div className="detailkopf">
          <p className="detailkopf__vorspann">Kanal der Fraktion</p>
          <h1 className="detailkopf__titel">{kanal.name}</h1>
        </div>
      )}

      <div className="chat">
        {/*
          Solange die Verschlüsselung anläuft, steht hier ein Satz — sonst
          hielte man einen leeren Kanal für einen stillen.
        */}
        {chat.stand === "verbinde" && (
          <p className="leise">Der verschlüsselte Chat wird verbunden …</p>
        )}

        {chat.stand === "fehler" && (
          <div className="karte">
            <p style={{ fontWeight: 600 }}>Der Chat ist gerade nicht erreichbar.</p>
            <p className="klein leise" style={{ marginTop: "var(--luft-2)" }}>
              {chat.fehler} Vorlagen, Notizen und Meinungsbild funktionieren weiterhin.
            </p>
          </div>
        )}

        {chat.bereit && beitraege.length === 0 && (
          <p className="leise">
            {aufgabe
              ? "Zu dieser Aufgabe hat sich noch niemand geäußert."
              : top
                ? "Zu diesem Punkt hat sich in der Fraktion noch niemand geäußert."
                : "In diesem Kanal steht noch nichts."}
          </p>
        )}

        {beitraege.map((n) => (
          <Beitrag
            key={n.id}
            nachricht={n}
            /*
              Im Kanalverlauf sagt der Verweis, worum es ging. In der
              Rückansicht wäre er überflüssig — dort ist der Punkt bereits die
              Überschrift.
            */
            mitBezug={!top && !aufgabe}
            aufTextstelle={zurTextstelle}
            aufBezug={aufBezug}
          />
        ))}
        <div ref={endeRef} />
      </div>

      <div className="verfassen">
        <div className="verfassen__karte">
          {zitat && (
            <div className="verfassen__zitat">
              <span style={{ flex: 1 }}>„{zitat.zitat}“</span>
              <button
                className="knopf knopf--schlicht"
                style={{ minHeight: "2.25rem", padding: "0 0.5rem" }}
                aria-label="Zitat entfernen"
                onClick={zitatLoeschen}
              >
                <Kreuz groesse={18} />
              </button>
            </div>
          )}

          {/*
            Wer aus einem Tagesordnungspunkt heraus schreibt, muss sehen, wohin
            der Beitrag geht. Der Kanal ist nicht der Punkt — er ist größer,
            und die ganze Fraktion liest mit.
          */}
          {(top || aufgabe) && (
            <p className="verfassen__ziel">
              Geht in den Kanal <strong>{kanal.name}</strong>, mit Verweis auf{" "}
              {aufgabe ? `die Aufgabe „${kuerze(aufgabe.titel)}“` : `TOP ${top.nummer}`}.
            </p>
          )}

          <textarea
            ref={feldRef}
            className="verfassen__feld"
            placeholder={
              zitat ? "Was fällt dir zu dieser Stelle auf?" : "Beitrag für die Fraktion schreiben …"
            }
            value={entwurf}
            onChange={(e) => setzeEntwurf(e.target.value)}
          />

          {sendefehler && (
            <p className="klein" style={{ color: "var(--ampel-nein)", marginBottom: "var(--luft-2)" }}>
              {sendefehler}
            </p>
          )}

          <div className="verfassen__zeile">
            <span className="klein leise" style={{ marginRight: "auto" }}>
              Schreibt als {ich.name} · verschlüsselt
            </span>
            <button
              className="knopf"
              onClick={senden}
              disabled={(!entwurf.trim() && !zitat) || !chat.bereit}
            >
              Senden
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function kuerze(text, laenge = 40) {
  return text.length <= laenge ? text : `${text.slice(0, laenge).trimEnd()} …`;
}

function Beitrag({ nachricht, mitBezug, aufTextstelle, aufBezug }) {
  const { stammdaten, zustand, ich } = useDemo();
  const eigen = nachricht.verfasserId === ich.id;
  const wer = nameVon(stammdaten, nachricht.verfasserId, ich.id);
  const annotation = nachricht.annotationId
    ? zustand.annotationen.find((a) => a.id === nachricht.annotationId)
    : null;
  const bezug = nachricht.topId
    ? stammdaten.tagesordnung.find((t) => t.id === nachricht.topId)
    : null;
  const sitzung = bezug ? stammdaten.sitzungen[bezug.sitzungId] : null;
  const aufgabe = nachricht.aufgabeId
    ? zustand.aufgaben.find((a) => a.id === nachricht.aufgabeId)
    : null;

  return (
    <div className={`beitrag ${eigen ? "beitrag--eigen" : ""}`}>
      <span className="beitrag__wer">
        {wer} · {kurzesDatum(nachricht.zeit)}, {uhrzeit(nachricht.zeit)}
      </span>
      <div className="beitrag__blase">
        {/*
          Die Adresse des Beitrags — und zugleich der Weg dorthin.

          Die Sitzungsnummer steht vorn, weil sie den Punkt erst eindeutig
          macht: „TOP 6“ allein gibt es in jeder Sitzung, und derselbe Vorgang
          kommt mehrfach auf die Tagesordnung. In einem Kanal, der die ganze
          Wahlperiode trägt, ist ohne die Nummer nicht zu erkennen, wovon die
          Rede war.

          Anklickbar, weil ein Verweis, dem man nicht folgen kann, keiner ist.
        */}
        {mitBezug && aufgabe && (
          <button
            className="beitrag__bezug"
            onClick={() => aufBezug && aufBezug({ art: "aufgabe", id: aufgabe.id })}
            title="Aufgabe öffnen"
          >
            Aufgabe: {kuerze(aufgabe.titel)}
            <PfeilRechts groesse={13} />
          </button>
        )}

        {mitBezug && bezug && !aufgabe && (
          <button
            className="beitrag__bezug"
            onClick={() => aufBezug && aufBezug({ art: "top", top: bezug })}
            title={`Zu TOP ${bezug.nummer} springen`}
          >
            {sitzung && sitzung.nummer ? `${sitzung.nummer}. Sitzung · ` : ""}
            TOP {bezug.nummer} · {bezug.titel}
            <PfeilRechts groesse={13} />
          </button>
        )}
        {annotation && <blockquote className="beitrag__zitat">„{annotation.zitat}“</blockquote>}
        <p>{nachricht.text}</p>
        {annotation && (
          <button className="beitrag__sprung" onClick={() => aufTextstelle(annotation)}>
            <Textmarker groesse={16} />
            Zur Textstelle in der Vorlage
          </button>
        )}
      </div>
    </div>
  );
}
